import axios from 'axios'
import { clearAuthSession, getAuthToken, saveAuthSession } from './auth'

const request = axios.create({
  baseURL: process.env.VUE_APP_API_BASE || '',
  // 首次（冷）聚合查询在大数据量下可能较慢，放宽超时避免请求被前端提前中断
  timeout: 120000
})

let unauthorizedHandler = null

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler
}

request.interceptors.request.use(config => {
  const token = getAuthToken()
  if (token) {
    config.headers['X-Auth-Token'] = token
  }
  return config
})

request.interceptors.response.use(response => {
  const body = response.data
  if (body && body.code === 0) {
    return body.data
  }
  if (body && body.code === 401 && unauthorizedHandler) {
    unauthorizedHandler(body.message || '登录已失效，请重新登录')
  }
  return Promise.reject(new Error((body && body.message) || '请求失败'))
}, error => {
  const body = error && error.response && error.response.data
  if (body && body.code === 401 && unauthorizedHandler) {
    unauthorizedHandler(body.message || '登录已失效，请重新登录')
  }
  return Promise.reject(new Error((body && body.message) || (error && error.message) || '请求失败'))
})

export function login(data) {
  return request.post('/api/auth/login', data).then(session => {
    saveAuthSession(session)
    return session
  })
}

export function sendSmsCode(data) {
  return request.post('/api/auth/sms-code', data)
}

export function getCaptcha() {
  return request.get('/api/auth/captcha')
}

export function logout() {
  return request.post('/api/auth/logout').finally(() => {
    clearAuthSession()
  })
}

export function getMe() {
  return request.get('/api/auth/me')
}

export function createApply(data) {
  return request.post('/api/opinion/task-applies', data)
}

export function estimateApply(data) {
  return request.post('/api/opinion/task-applies/estimate', data, { timeout: 120000 })
}

// 监控对象关键词组合智能推荐
export function recommendKeywordSchemes(data) {
  return request.post('/api/opinion/keyword-recommend', data)
}

// 前台任务申请辅助（多轮对话，不落库）
export function assistTaskApply(data) {
  return request.post('/api/opinion/apply-assist', data)
}

// 卡片确认后重算下一步，不调 Agent
export function evaluateTaskApply(data) {
  return request.post('/api/opinion/apply-assist/evaluate', { draft: data && data.draft })
}

export function updateApply(id, data) {
  return request.put(`/api/opinion/task-applies/${id}`, data)
}

export function listApplies(status) {
  return request.get('/api/opinion/task-applies', { params: { status } })
}

export function pauseApply(id, data) {
  return request.post(`/api/opinion/task-applies/${id}/pause`, data || {})
}

export function resumeApply(id, data) {
  return request.post(`/api/opinion/task-applies/${id}/resume`, data || {})
}

export function listTasks() {
  return request.get('/api/opinion/tasks')
}

export function getTaskSummary(taskId) {
  return request.get(`/api/opinion/tasks/${taskId}/summary`)
}

export function getOverview(params) {
  return request.get('/api/opinion/overview', { params })
}

export function getDataRange() {
  return request.get('/api/opinion/data-range')
}

export function getRisk(params) {
  return request.get('/api/opinion/risk', { params })
}

export function getKol(params) {
  return request.get('/api/opinion/kol', { params })
}

export function getEvents(params) {
  return request.get('/api/opinion/events', { params })
}

export function getBrands(params) {
  return request.get('/api/opinion/brands', { params })
}

export function listWatchedBrands() {
  return request.get('/api/opinion/brand-watch')
}

export function addWatchedBrand(name) {
  return request.post('/api/opinion/brand-watch', { name })
}

export function removeWatchedBrand(id) {
  return request.delete(`/api/opinion/brand-watch/${id}`)
}

export function getBrandAnalysis(params) {
  return request.get('/api/opinion/brand-analysis', { params })
}

export function createReportJob(data) {
  return request.post('/api/opinion/reports/jobs', data)
}

export function getReportJob(id) {
  return request.get(`/api/opinion/reports/jobs/${id}`)
}

export function listReportJobs() {
  return request.get('/api/opinion/reports/jobs')
}

export function getMonitor(params) {
  return request.get('/api/opinion/monitor', { params })
}

export function listWorkOrders(status) {
  return request.get('/api/opinion/work-orders', { params: { status } })
}

export function listWorkOrderLogs(id) {
  return request.get(`/api/opinion/work-orders/${id}/logs`)
}

export function createWorkOrder(data) {
  return request.post('/api/opinion/work-orders', data)
}

export function assignWorkOrder(id, data) {
  return request.post(`/api/opinion/work-orders/${id}/assign`, data)
}

export function processWorkOrder(id, data) {
  return request.post(`/api/opinion/work-orders/${id}/process`, data)
}

export function reviewWorkOrder(id, data) {
  return request.post(`/api/opinion/work-orders/${id}/review`, data)
}

export function closeWorkOrder(id, data) {
  return request.post(`/api/opinion/work-orders/${id}/close`, data)
}

export function listResults(taskId, pageNo, pageSize) {
  return request.get(`/api/opinion/tasks/${taskId}/results`, { params: { pageNo, pageSize } })
}

export function getResultDetail(taskId, resultId, data) {
  return request.post(`/api/opinion/tasks/${taskId}/results/${resultId}/detail`, data)
}

export function listViewLogs(taskId, resultId) {
  return request.get(`/api/opinion/tasks/${taskId}/results/${resultId}/view-logs`)
}

// ===== 租户管理（业务用户 token + /api/group/**；租户管理员=GROUP_LEADER）=====

export function getGroupInfo() {
  return request.get('/api/group/info')
}

export function renameGroup(data) {
  return request.put('/api/group/info', data)
}

export function listGroupMembers() {
  return request.get('/api/group/members')
}

export function createGroupMember(data) {
  return request.post('/api/group/members', data)
}

export function updateGroupMember(id, data) {
  return request.put(`/api/group/members/${id}`, data)
}

export function disableGroupMember(id) {
  return request.delete(`/api/group/members/${id}`)
}

export function getGroupUsage() {
  return request.get('/api/group/usage')
}

export function listGroupApplies() {
  return request.get('/api/group/applies')
}

export function approveGroupApply(id, data) {
  return request.post(`/api/group/applies/${id}/approve`, data)
}

export function rejectGroupApply(id, data) {
  return request.post(`/api/group/applies/${id}/reject`, data)
}

export function pauseGroupApply(id, data) {
  return request.post(`/api/group/applies/${id}/pause`, data || {})
}

export function resumeGroupApply(id, data) {
  return request.post(`/api/group/applies/${id}/resume`, data || {})
}

export function listMemberBrands(userId) {
  return request.get(`/api/group/members/${userId}/brands`)
}

export function addMemberBrand(userId, name) {
  return request.post(`/api/group/members/${userId}/brands`, { name })
}

export function removeMemberBrand(userId, brandId) {
  return request.delete(`/api/group/members/${userId}/brands/${brandId}`)
}
